import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.5fairiescleaning.com';
const SITE_NAME = '5 Fairies Cleaning Services';
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og-image.jpg`;

/**
 * Seo — per-page document head for public pages.
 *
 * Overrides the static defaults in public/index.html so each route gets a
 * unique title, description, canonical URL, and share-card metadata.
 *
 * Props:
 *   title       – page title (SITE_NAME is appended automatically)
 *   description – meta description for this page
 *   path        – canonical path, e.g. "/residential-cleaning"
 *   image       – absolute URL for the share image (defaults to the site card)
 *   noindex     – true for private/tokenized pages that must stay out of search
 *   jsonLd      – optional schema.org object (or array of objects) injected as a
 *                 <script type="application/ld+json"> block for this page
 */
const Seo = ({ title, description, path = '/', image = DEFAULT_OG_IMAGE, noindex = false, jsonLd = null }) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const canonical = `${SITE_URL}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />

      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export default Seo;
